import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, Loader2, Copy, ExternalLink } from "lucide-react";
import { API_URL } from "@/utils/apiConfig";
import { ExternalSiteIntegrationSection } from "./ExternalSiteIntegrationSection";
import { InstagramIntegrationCard } from "./InstagramIntegrationCard";

export const IntegracionesTab = ({ authAxios, restaurant, onUpdate }) => {
  const ig = restaurant?.integrations || {};
  const [vendusKey, setVendusKey] = useState(ig.vendus_api_key || "");
  const [gaId, setGaId] = useState(ig.google_analytics_id || "");
  const [gtmId, setGtmId] = useState(ig.google_tag_manager_id || "");
  const [openaiKey, setOpenaiKey] = useState(ig.openai_key || "");
  const [uberUser, setUberUser] = useState(ig.uber_user || "");
  const [uberClientId, setUberClientId] = useState(ig.uber_client_id || "");
  const [uberSecret, setUberSecret] = useState(ig.uber_client_secret || "");
  const [fbPixel, setFbPixel] = useState(ig.facebook_pixel_id || "");
  const [fbToken, setFbToken] = useState(ig.facebook_access_token || "");
  const [saving, setSaving] = useState(false);
  const [vendusStatus, setVendusStatus] = useState(null);
  const [vendusLoading, setVendusLoading] = useState(false);

  const testVendus = async () => {
    setVendusLoading(true);
    // Save first so the key is in DB before testing
    try {
      await authAxios.patch("/restaurant", {
        integrations: {
          vendus_api_key: vendusKey, google_analytics_id: gaId, google_tag_manager_id: gtmId,
          openai_key: openaiKey, uber_user: uberUser, uber_client_id: uberClientId,
          uber_client_secret: uberSecret, facebook_pixel_id: fbPixel,
          facebook_access_token: fbToken,
        }
      });
    } catch { /* ignore save errors, test will show the real error */ }
    try {
      const res = await authAxios.get("/vendus/test-connection");
      setVendusStatus(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.message || e?.message || "Erro de conexao";
      setVendusStatus({ connected: false, message: msg });
    }
    setVendusLoading(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await authAxios.patch("/restaurant", {
        integrations: {
          vendus_api_key: vendusKey, google_analytics_id: gaId, google_tag_manager_id: gtmId,
          openai_key: openaiKey, uber_user: uberUser, uber_client_id: uberClientId,
          uber_client_secret: uberSecret, facebook_pixel_id: fbPixel,
          facebook_access_token: fbToken,
        }
      });
      toast.success("Integraciones guardadas");
      onUpdate();
    } catch { toast.error("Error guardando"); }
    setSaving(false);
  };

  return (
    <>
      <Card className="bg-card border-border/50 border-l-4 border-l-emerald-500">
        <CardHeader>
          <CardTitle className="uppercase">Vendus (Facturacion Fiscal)</CardTitle>
          <CardDescription>Software certificado de facturacion para Espana y Portugal.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Clave API</Label>
            <Input type="password" value={vendusKey} onChange={e => setVendusKey(e.target.value)} placeholder="17c363e2dc..." className="bg-secondary border-transparent font-mono" data-testid="vendus-api-key" />
            <p className="text-xs text-muted-foreground">Obtenga su clave en vendus.es &rarr; Configuracion &rarr; Usuarios &rarr; API</p>
          </div>
          {vendusKey && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-emerald-600">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                Clave API configurada
              </div>
              <Button variant="outline" size="sm" onClick={testVendus} disabled={vendusLoading} data-testid="test-vendus-btn">
                {vendusLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Probar Conexion
              </Button>
              {vendusStatus && (
                <div className={`p-3 rounded-lg text-sm ${vendusStatus.connected ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`} data-testid="vendus-status">
                  <p className={`font-medium ${vendusStatus.connected ? "text-emerald-700" : "text-red-700"}`}>
                    {vendusStatus.connected ? "Conectado" : "Error"}: {vendusStatus.message}
                  </p>
                  {vendusStatus.warning && <p className="text-orange-600 mt-2 text-xs">{vendusStatus.warning}</p>}
                  {vendusStatus.registers?.length > 0 && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      <p className="font-medium">Cajas registradoras:</p>
                      {vendusStatus.registers.map((r, i) => (
                        <p key={i}>{r.title} - Tipo: <span className={r.type === "api" ? "text-emerald-600 font-bold" : "text-orange-600 font-bold"}>{r.type.toUpperCase()}</span></p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="uppercase">Google Analytics</CardTitle></CardHeader>
        <CardContent><div className="space-y-2"><Label>ID de rastreo</Label><Input value={gaId} onChange={e => setGaId(e.target.value)} placeholder="G-XXXXXXXXXX" className="bg-secondary border-transparent font-mono" /></div></CardContent>
      </Card>

      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="uppercase">Google Tag Manager</CardTitle></CardHeader>
        <CardContent><div className="space-y-2"><Label>ID del contenedor</Label><Input value={gtmId} onChange={e => setGtmId(e.target.value)} placeholder="GTM-XXXXXXX" className="bg-secondary border-transparent font-mono" /></div></CardContent>
      </Card>

      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="uppercase">OpenAI</CardTitle></CardHeader>
        <CardContent><div className="space-y-2"><Label>Secret Key</Label><Input type="password" value={openaiKey} onChange={e => setOpenaiKey(e.target.value)} placeholder="sk-..." className="bg-secondary border-transparent font-mono" /></div></CardContent>
      </Card>

      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="uppercase">Uber Direct</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>ID usuario</Label><Input value={uberUser} onChange={e => setUberUser(e.target.value)} placeholder="Usuario" className="bg-secondary border-transparent" /></div>
            <div className="space-y-2"><Label>ID cliente</Label><Input value={uberClientId} onChange={e => setUberClientId(e.target.value)} placeholder="Client ID" className="bg-secondary border-transparent font-mono" /></div>
          </div>
          <div className="space-y-2"><Label>Client Secret</Label><Input type="password" value={uberSecret} onChange={e => setUberSecret(e.target.value)} placeholder="Client Secret" className="bg-secondary border-transparent font-mono" /></div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="uppercase">Facebook</CardTitle>
          <CardDescription>Pixel ID + Conversions API (server-side) + Catálogo de Produtos para Dynamic Ads.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Pixel ID</Label>
            <Input value={fbPixel} onChange={e => setFbPixel(e.target.value)} placeholder="919266767566089" className="bg-secondary border-transparent font-mono" data-testid="facebook-pixel-input" />
          </div>
          <div className="space-y-2">
            <Label>Access Token (Conversions API)</Label>
            <Input type="password" value={fbToken} onChange={e => setFbToken(e.target.value)} placeholder="EAAZAM6oH3P..." className="bg-secondary border-transparent font-mono" data-testid="facebook-access-token-input" />
            <p className="text-xs text-muted-foreground">Events Manager → Conjuntos de datos → Configurações → Configurar integração direta</p>
          </div>
          {fbPixel && fbToken && (
            <div className="flex items-center gap-2 text-sm text-emerald-600">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              Pixel + Conversions API configurados
            </div>
          )}
          {/* Facebook Product Catalog Feed */}
          {restaurant?.id && (() => {
            const catalogUrl = `${API_URL}/api/public/facebook/catalog/${restaurant.id}.xml`;
            return (
              <div className="space-y-2 pt-2 border-t border-border/50">
                <Label className="flex items-center gap-2">
                  URL do feed do catálogo de produtos
                  <span className="text-[10px] font-normal text-muted-foreground">(Facebook → Commerce Manager → Catálogo)</span>
                </Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-muted rounded px-3 py-2 truncate font-mono text-blue-600 select-all">
                    {catalogUrl}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => { navigator.clipboard.writeText(catalogUrl); toast.success("URL copiada!"); }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => window.open(catalogUrl, "_blank")}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Adicione esta URL no Facebook Business → Commerce Manager → Catálogos → Adicionar itens → Feed de dados
                </p>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      <InstagramIntegrationCard authAxios={authAxios} restaurant={restaurant} />

      <ExternalSiteIntegrationSection authAxios={authAxios} />

      <Button className="glow-orange" onClick={save} disabled={saving} data-testid="save-integraciones-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        Guardar Integraciones
      </Button>
    </>
  );
};
